import { Box, Button, Divider, Stack, Tooltip, Typography } from "@mui/material"
import {
  type ServiceAccount,
  useCreateServiceAccountApiKeyMutation,
  useDeleteServiceAccountApiKeyMutation,
} from "../../services/serviceAccountsApi"
import { ServiceAccountApiKeysTable } from "./ServiceAccountApiKeysTable"
import {
  getCreateApiKeyMutationCacheKey,
  getDefaultServiceAccountApiKeyExpiration,
  getDeleteApiKeyMutationCacheKey,
  getErrorMessage,
} from "./utils"

type ServiceAccountRowProps = {
  activeOrgId: string | null
  isOrgServiceAdmin: boolean
  index: number
  onApiKeyActionStarted: () => void
  onApiKeyDeleted: (message: string) => void
  onApiKeyError: (message: string) => void
  onApiKeyGenerated: (message: string, apiKeyValue: string) => void
  serviceAccount: ServiceAccount
}

export function ServiceAccountRow({
  activeOrgId,
  isOrgServiceAdmin,
  index,
  onApiKeyActionStarted,
  onApiKeyDeleted,
  onApiKeyError,
  onApiKeyGenerated,
  serviceAccount,
}: ServiceAccountRowProps) {
  const createMutationCacheKey = getCreateApiKeyMutationCacheKey(
    serviceAccount.service_account_id
  )
  const deleteMutationCacheKey = getDeleteApiKeyMutationCacheKey(
    serviceAccount.service_account_id
  )

  const [createServiceAccountApiKey, { isLoading: isCreatePending }] =
    useCreateServiceAccountApiKeyMutation({
      fixedCacheKey: createMutationCacheKey,
    })
  const [, { isLoading: isDeletePending }] =
    useDeleteServiceAccountApiKeyMutation({
      fixedCacheKey: deleteMutationCacheKey,
    })
  const manageServiceAccountsTooltip = isOrgServiceAdmin
    ? ""
    : "Requires the organization service admin role."

  const handleCreateApiKey = async () => {
    if (!isOrgServiceAdmin) {
      onApiKeyError(
        "You need the organization service admin role to add API keys."
      )
      return
    }

    if (!activeOrgId) {
      onApiKeyError(
        "Choose an active organization before generating an API key."
      )
      return
    }

    onApiKeyActionStarted()

    try {
      const response = await createServiceAccountApiKey({
        org_id: activeOrgId,
        service_account: serviceAccount.service_account_id,
        expires_at: getDefaultServiceAccountApiKeyExpiration(),
      }).unwrap()

      onApiKeyGenerated(
        `Generated a new API key for "${serviceAccount.label}".`,
        response.api_key
      )
    } catch (error) {
      onApiKeyError(
        getErrorMessage(error) ?? "Unable to generate a new API key."
      )
    }
  }

  return (
    <Box component="li" sx={{ listStyle: "none" }}>
      {index > 0 && <Divider component="div" />}
      <Stack spacing={2} sx={{ py: 2 }}>
        <Typography variant="subtitle1">{serviceAccount.label}</Typography>

        <ServiceAccountApiKeysTable
          activeOrgId={activeOrgId}
          isOrgServiceAdmin={isOrgServiceAdmin}
          onApiKeyActionStarted={onApiKeyActionStarted}
          onApiKeyDeleted={onApiKeyDeleted}
          onApiKeyError={onApiKeyError}
          serviceAccount={serviceAccount}
        />

        <Stack direction="row" justifyContent="flex-start">
          <Tooltip title={manageServiceAccountsTooltip}>
            <span>
              <Button
                disabled={
                  isCreatePending || isDeletePending || !isOrgServiceAdmin
                }
                onClick={() => {
                  void handleCreateApiKey()
                }}
                variant="outlined"
              >
                {isCreatePending ? "Generating..." : "Generate New API Key"}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  )
}
